import { ab } from './interface'
import { Experiment } from './types'
import type {
  TrialByPlanV2_18369Variant,
  IosExternalLinkPayment16218Variant,
  NullExperiment17503Variant,
  FirstGuideActivation17817Variant,
  AnnotatorRewrite18170Variant,
  PasswordlessSignup18304Variant,
  FirstLaunchWantToRead18947Variant,
  CancelSaveFlow17977Variant,
  PartnershipHesitationDiscount19142Variant,
  SummaryPagePricingExitIntent19129Variant,
  OnboardingChecklist19055Variant,
  ReturnToValueBackend19051Variant,
  HomepageHeroGoalSelector19253Variant,
  ContinueReading19038Variant,
  GuideCompletion19383Variant,
  MiniPlayer19040Variant,
  SaveForLater1Library20321Variant,
  SaveForLater2Save20322Variant,
  OnboardingPlanCcCapture20192Variant,
  HardGateBilling20516Variant,
} from './types'

import { runTextUpdates } from '@/services/ab/13448_ab_experiment'
import { auth } from '@/services/auth'
import { backend } from '@/services/backend'
import { platform } from '@/services/platform'
import { platformData } from '@/services/platform.capacitor'
import { getIosStorefrontLocation } from '@/services/capacitor.storefrontLocationPlugin'
import { Sentry } from '@/services/sentry'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { resetCustomListsCache, resetDocsUserDataCache } from '@/helpers/cache'
import { pushABExperimentsToDataLayer } from '@/analytics/gtm'
import { ChecklistStratum } from '@/models/checklist.types'
import { initChecklist } from '@/models/checklist'
import { playerSession } from '@/services/player'

/**
 * Initialize CloudFront experiments.
 *
 * Check A/B testing cookies set by CloudFront, send backend
 * requests and update frontend a/b data storage.
 * Pushes AB experiments to GTM data layer when done.
 */
export async function initAB(): Promise<void> {
  await newCopy13448Experiment(Experiment.new_copy_pdf_v2_13448, '/pdf')
  await aiGuideFlow13747()
  await aiOldVsPbp18613()
  await goalProblemPages18788()
  await homepageHeroGoalSelector19253()
  pushABExperimentsToDataLayer()
}

/**
 * Initializes Public Summary pages specific CloudFront experiments.
 * Calls {@link initAB}
 */
export async function initSummaryAB(): Promise<void> {
  // Add public summary specific experiment here

  await initAB()
}

/**
 * Initializes PDF pages specific CloudFront experiments.
 * Calls {@link initAB}
 */
export async function initPdfAB(): Promise<void> {
  // Add PDF page specific experiment here

  await initAB()
}

/**
 * Feature flag to enable/disable Google Billing. Disabled by default.
 *
 * This is not a real A/B experiment, i.e. the experiment is not activated on
 * the backend.
 */
export function googleBillingEnabled(): boolean {
  return ab.experimentVariant(Experiment.google_billing_7517) === 'yes'
}

/**
 * Apply the text changes on the public pages
 *
 * @param {Experiment} experiment - The A/B experiment containing the text changes.
 * @param {string} allowedUrlExpression - A regular expression to match allowed URLs,
 * on which the text changes should be applied.
 */
export async function newCopy13448Experiment(
  experiment: Experiment,
  allowedUrlExpression: string,
): Promise<void> {
  const pathname = window.location.pathname
  const isTranslatedPage = pathname.includes(`/es${allowedUrlExpression}/`)

  if (!pathname.includes(allowedUrlExpression) || isTranslatedPage) {
    // Only start experiment on non-translated pdf pages
    return
  }

  let result = ab.experimentVariant(experiment)

  if (result === null) {
    result = await ab.start(experiment)
  }

  if (result === 'yes') {
    runTextUpdates(experiment)
  }
}

/**
 * Get the variant of the ai_label_13537 experiment.
 * Returns boolean:
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export function aiLabel13537ExperimentVariant(): boolean {
  return ab.experimentVariant(Experiment.ai_label_13537) === 'yes'
}

/**
 * Decide wheter or not to render the ai content label component in
 * the book preview and summary pages.
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export async function aiLabel13537Experiment(): Promise<boolean> {
  let result = ab.experimentVariant(Experiment.ai_label_13537)

  if (result === null) {
    result = await ab.start(Experiment.ai_label_13537)
  }

  return result === 'yes'
}

/*
 * Sync the ai_guide_flow_13747 experiment assignment with CloudFront.
 */
export async function aiGuideFlow13747(): Promise<void> {
  await ab.startCloudFrontExperiment(
    Experiment.ai_guide_flow_13747,
    'X-AB-AI-Guide-Flow-13747',
  )
}

/*
 * Sync the ai_old_vs_pbp_18613 experiment assignment with CloudFront.
 */
export async function aiOldVsPbp18613(): Promise<void> {
  await ab.startCloudFrontExperiment(
    Experiment.ai_old_vs_pbp_18613,
    'X-AB-AI-Old-Vs-Pbp-18613',
  )
}

/**
 * Trial by Plan V2 experiment.
 *
 * A - Control: current experience without the trial by plan feature.
 * B - Variant: monthly and annual plans have different trial lengths
 *     (no trial for monthly, 14 days for annual).
 *
 * Paused (see issue #18910): no longer bucketing new users into the
 * backend experiment, everyone gets the control variant. Users already
 * assigned to a variant keep seeing it, from local storage.
 */
export async function trialByPlanV2_18369Experiment(): Promise<TrialByPlanV2_18369Variant> {
  let result = ab.experimentVariant(
    Experiment.exp_trial_by_plan_v2_18369,
  ) as TrialByPlanV2_18369Variant

  const isAndroid = await platform.isAndroid()
  const isNativeIOSApp = await platform.isNativeIOSApp()

  if (isAndroid || isNativeIOSApp) {
    return null
  }

  if (result === null) {
    result = 'A'
  }

  return result
}

export function trialByPlanV2_18369Variant(): TrialByPlanV2_18369Variant {
  return ab.experimentVariant(
    Experiment.exp_trial_by_plan_v2_18369,
  ) as TrialByPlanV2_18369Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export async function iosExternalLinkPayment16218Experiment(): Promise<IosExternalLinkPayment16218Variant> {
  await platformData.init()
  const appBuild = platformData.appBuild
  // We need to get the storefront location for the experiment
  // and it's not available in builds < 60.
  if (appBuild < 60) {
    return null
  }
  const storefrontLocation = await getIosStorefrontLocation()
  // External payments are allowed in the US storefront only.
  if (storefrontLocation !== 'USA') {
    return null
  }

  // Check variant in the local storage
  let result = ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  )

  // Start the experiment
  if (result === null) {
    result = await ab.start(Experiment.exp_ios_external_link_payment_16218)
  }

  return result as IosExternalLinkPayment16218Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export function iosExternalLinkPayment16218Variant(): IosExternalLinkPayment16218Variant {
  return ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  ) as IosExternalLinkPayment16218Variant
}

/**
 * Start the exp_null_experiment_17503 experiment on signup page visit.
 *
 * A - Control, no changes.
 * B - Variant, no changes.
 */
export async function signupPageNullExperiment17503Experiment(): Promise<NullExperiment17503Variant> {
  let result = ab.experimentVariant(Experiment.exp_null_experiment_17503)

  if (result === null) {
    result = await ab.start(Experiment.exp_null_experiment_17503)
  }

  return result as NullExperiment17503Variant
}

/**
 * Start the exp_first_guide_activation_17817 experiment at the end of
 * onboarding.
 *
 * A - Control: existing flow after onboarding.
 * B - Variant: display "first guide" popup after onboarding.
 */
export async function firstGuideActivation17817Experiment(): Promise<FirstGuideActivation17817Variant> {
  let result = ab.experimentVariant(Experiment.exp_first_guide_activation_17817)

  if (result === null) {
    result = await ab.start(Experiment.exp_first_guide_activation_17817)
  }

  return result as FirstGuideActivation17817Variant
}

/**
 * Get the variant for the exp_first_guide_activation_17817 experiment.
 */
export function firstGuideActivation17817Variant(): FirstGuideActivation17817Variant {
  return ab.experimentVariant(
    Experiment.exp_first_guide_activation_17817,
  ) as FirstGuideActivation17817Variant
}

/**
 * Start the exp_annotator_rewrite_18170 experiment when an annotator loads.
 *
 * A - Control: existing annotator.js implementation.
 * B - Variant: Vue annotator rewrite.
 */
export async function annotatorRewrite18170Experiment(): Promise<AnnotatorRewrite18170Variant> {
  let result = ab.experimentVariant(Experiment.exp_annotator_rewrite_18170)

  if (result === null) {
    result = await ab.start(Experiment.exp_annotator_rewrite_18170)
  }

  return result as AnnotatorRewrite18170Variant
}

/**
 * Start the exp_passwordless_signup_18304 experiment.
 *
 * A - Control: existing signup flow with password.
 * B - Variant: passwordless signup flow.
 */
export async function passwordlessSignup18304Experiment(): Promise<PasswordlessSignup18304Variant> {
  return passwordlessSignup18304Variant()
}

/**
 * Get the variant for the exp_passwordless_signup_18304 experiment.
 */
export function passwordlessSignup18304Variant(): PasswordlessSignup18304Variant {
  const result = ab.experimentVariant(
    Experiment.exp_passwordless_signup_18304,
  ) as PasswordlessSignup18304Variant

  if (!result && window.__UNIT_TESTING) {
    return 'A'
  }

  return result ?? 'B'
}

/**
 * Start the exp_cancel_save_flow_17977 experiment when the user clicks the
 * cancel-subscription link.
 *
 * A - Control: existing cancel popup, no save flow.
 * B - Variant: show the save flow on "End my personal growth".
 */
export async function cancelSaveFlow17977Experiment(): Promise<CancelSaveFlow17977Variant> {
  let result = ab.experimentVariant(Experiment.exp_cancel_save_flow_17977)

  if (result === null) {
    result = await ab.start(Experiment.exp_cancel_save_flow_17977)
  }

  return result as CancelSaveFlow17977Variant
}

/**
 * Get the assigned variant for the cancel_save_flow_17977 experiment.
 */
export function cancelSaveFlow17977Variant(): CancelSaveFlow17977Variant {
  return ab.experimentVariant(
    Experiment.exp_cancel_save_flow_17977,
  ) as CancelSaveFlow17977Variant
}

/**
 * Start the exp_first_launch_want_to_read_18947 experiment.
 *
 * A - Control: create a separate First Reading List.
 * B - Variant: add arrival and onboarding books to Want to Read.
 */
export async function firstLaunchWantToRead18947Experiment(): Promise<FirstLaunchWantToRead18947Variant> {
  let result = ab.experimentVariant(
    Experiment.exp_first_launch_want_to_read_18947,
  )

  if (result === null) {
    result = await ab.start(Experiment.exp_first_launch_want_to_read_18947)
  }

  return result as FirstLaunchWantToRead18947Variant
}

/**
 * Assign experiment variant for #18947 and finish any pending first list
 * initialization.
 * Returns `null` when assignment or initialization fails.
 */
export async function initializeFirstLaunchWantToRead18947(): Promise<FirstLaunchWantToRead18947Variant | null> {
  const variant = await firstLaunchWantToRead18947Experiment()
  if (variant === null) {
    return null
  }

  try {
    const initialized = await backend.initializeFirstLaunchReadingList()
    if (initialized) {
      // Initialization can add several books to a list. Remove both the cached
      // lists and the cached per-book list memberships so later pages load the
      // initialized data from the backend.
      await Promise.all([resetCustomListsCache(), resetDocsUserDataCache()])
    }
    if (variant === 'B') {
      // Remove the book covers from local storage
      // to prevent the reading list widget from being
      // shown in the Discover page
      storage.removeItem(StorageKeys.questionnaireBooks)
    }
  } catch (error) {
    Sentry.captureException(error)
    return null
  }

  return variant
}

export const GOAL_PROBLEM_PAGE_18788_EXPERIMENTS = [
  {
    experiment: Experiment.exp_emotional_intelligence_18788,
    cookieName: 'X-AB-Emotional-Intelligence-18788',
    entryPath: '/lp/emotional-intelligence',
    controlPath: '/lp/emotional-intelligence-eq',
  },
  {
    experiment: Experiment.exp_career_ascension_18788,
    cookieName: 'X-AB-Career-Ascension-18788',
    entryPath: '/lp/career-ascension',
    controlPath: '/lp/career-ascension-eq',
  },
  {
    experiment: Experiment.exp_intellectual_mastery_18788,
    cookieName: 'X-AB-Intellectual-Mastery-18788',
    entryPath: '/lp/intellectual-mastery',
    controlPath: '/lp/intellectual-mastery-eq',
  },
  {
    experiment: Experiment.exp_discipline_systems_18788,
    cookieName: 'X-AB-Discipline-Systems-18788',
    entryPath: '/lp/discipline-systems',
    controlPath: '/lp/discipline-systems-eq',
  },
] as const

function isGoalProblemPage18788Path(
  pathname: string,
  entryPath: string,
  controlPath: string,
): boolean {
  return [entryPath, controlPath].some(
    (path) => pathname === path || pathname === `${path}/`,
  )
}

/**
 * Sync the goal problem page experiment assignments with CloudFront.
 */
export async function goalProblemPages18788(): Promise<void> {
  const pathname = window.location.pathname

  for (const page of GOAL_PROBLEM_PAGE_18788_EXPERIMENTS) {
    if (
      !isGoalProblemPage18788Path(pathname, page.entryPath, page.controlPath)
    ) {
      continue
    }

    await ab.startCloudFrontExperiment(page.experiment, page.cookieName)
  }
}

/**
 * Sync the homepage hero goal selector experiment assignment with CloudFront.
 */
export async function homepageHeroGoalSelector19253(): Promise<void> {
  await ab.startCloudFrontExperiment(
    Experiment.exp_homepage_hero_goal_selector_19253,
    'X-AB-Homepage-Hero-Goal-Selector-19253',
  )
}

/**
 * Start the exp_billing_partnership_hesitation_redesign_19142 experiment.
 *
 * A - Control: existing promo banner, two-card layout, single CTA.
 * B - Variant: unified card redesign, reframed savings badge, hesitation
 *     modal - reusing the partner's existing discount rather than a new one.
 *
 * The caller checks eligibility (see isPartnershipHesitationDiscountEligible19142)
 * before calling this function.
 */
export async function partnershipHesitationDiscount19142Experiment(): Promise<PartnershipHesitationDiscount19142Variant> {
  let result = ab.experimentVariant(
    Experiment.exp_billing_partnership_hesitation_redesign_19142,
  )

  if (result === null) {
    result = await ab.start(
      Experiment.exp_billing_partnership_hesitation_redesign_19142,
    )
  }

  return result as PartnershipHesitationDiscount19142Variant
}

/**
 * Get the variant for the exp_billing_partnership_hesitation_redesign_19142
 * experiment.
 */
export function partnershipHesitationDiscount19142Variant(): PartnershipHesitationDiscount19142Variant {
  return ab.experimentVariant(
    Experiment.exp_billing_partnership_hesitation_redesign_19142,
  ) as PartnershipHesitationDiscount19142Variant
}

/**
 * Returns true if the user has the treatment variant in
 * exp_billing_partnership_hesitation_redesign_19142.
 */
export function partnershipHesitationDiscount19142Treatment(): boolean {
  return partnershipHesitationDiscount19142Variant() === 'B'
}

/**
 * Start the exp_summary_page_pricing_exit_intent_19129 experiment for eligible
 * free users on Discover, Doc Preview, and Summary pages.
 *
 * A - Control: current "Start Free Trial to Unlock" CTA
 * B - Variant: Annual / Monthly plan cards replace the unlock CTA; Select
 *     Plan opens SubscribePopup on the current page; exit-intent recovery
 *     modal.
 *
 */
export async function summaryPagePricingExitIntent19129Experiment(): Promise<SummaryPagePricingExitIntent19129Variant> {
  // This experiment is only run for free users
  if (!auth.isFreePlan()) {
    return null
  }

  // We'll skip the experiment on native apps
  if (await platform.isNativeApp()) {
    return null
  }

  let result = ab.experimentVariant(
    Experiment.exp_summary_page_pricing_exit_intent_19129,
  )

  if (result === null) {
    result = await ab.start(
      Experiment.exp_summary_page_pricing_exit_intent_19129,
    )
  }

  return result as SummaryPagePricingExitIntent19129Variant
}

/**
 * Get the variant for the exp_summary_page_pricing_exit_intent_19129 experiment.
 */
export function summaryPagePricingExitIntent19129Variant(): SummaryPagePricingExitIntent19129Variant {
  return ab.experimentVariant(
    Experiment.exp_summary_page_pricing_exit_intent_19129,
  ) as SummaryPagePricingExitIntent19129Variant
}

/**
 * Start the exp_onboarding_checklist_19055 experiment.
 *
 * A - Control: No Onboarding Checklist.
 * B - Variant: Onboarding Checklist.
 */
export async function onboardingChecklist19055Experiment(
  stratum: ChecklistStratum,
): Promise<OnboardingChecklist19055Variant> {
  let result = ab.experimentVariant(Experiment.exp_onboarding_checklist_19055)

  if (result === null) {
    result = await ab.start(Experiment.exp_onboarding_checklist_19055)
    if (result === 'B') {
      initChecklist(stratum)
    }
  }

  return result as OnboardingChecklist19055Variant
}

/**
 * Get the variant for the exp_onboarding_checklist_19055 experiment.
 */
export function onboardingChecklist19055Variant(): OnboardingChecklist19055Variant {
  return ab.experimentVariant(
    Experiment.exp_onboarding_checklist_19055,
  ) as OnboardingChecklist19055Variant
}

/**
 * Start the exp_return_to_value_backend_19051 experiment.
 *
 * A - Control.
 * B - Send daily notifications.
 */
export async function returnToValue19051Experiment(): Promise<ReturnToValueBackend19051Variant> {
  let result = ab.experimentVariant(
    Experiment.exp_return_to_value_backend_19051,
  )

  if (result === null) {
    result = await ab.start(Experiment.exp_return_to_value_backend_19051)
  }

  return result as ReturnToValueBackend19051Variant
}

/**
 * Get the variant for the exp_return_to_value_backend_19051 experiment.
 */
export function returnToValue19051Variant(): ReturnToValueBackend19051Variant {
  return ab.experimentVariant(
    Experiment.exp_return_to_value_backend_19051,
  ) as ReturnToValueBackend19051Variant
}

/**
 * Get the assigned variant for exp_homepage_hero_goal_selector_19253.
 *
 * A - Control: existing homepage hero, "Sign up for Free" CTA.
 * B - Variant: new hero with a 5-option goal selector, routes into
 *     onboarding, email capture stays in its current early position.
 * C - Variant: same new hero as B, email capture moves to the end of the
 *     onboarding flow.
 */
export function homepageHeroGoalSelector19253Variant(): HomepageHeroGoalSelector19253Variant {
  return ab.experimentVariant(
    Experiment.exp_homepage_hero_goal_selector_19253,
  ) as HomepageHeroGoalSelector19253Variant
}

/**
 * Get the variant for the exp_continue_reading_19038 experiment.
 *
 * A - Control: today's behaviour.
 * B - Variant: Continue Reading Foundations (epic #19038).
 */
export function continueReading19038Variant(): ContinueReading19038Variant {
  return ab.experimentVariant(
    Experiment.exp_continue_reading_19038,
  ) as ContinueReading19038Variant
}

/**
 * Get or start the guide completion experiment.
 *
 * A - Control: current guide completion behaviour.
 * B - Variant: show the new guide completion popup.
 */
export async function guideCompletion19383Experiment(): Promise<GuideCompletion19383Variant> {
  let result = ab.experimentVariant(Experiment.exp_guide_completion_19383)

  if (result === null) {
    result = await ab.start(Experiment.exp_guide_completion_19383)
  }

  return result as GuideCompletion19383Variant
}

/**
 * Get the assigned variant for the guide completion experiment.
 */
export function guideCompletion19383Variant(): GuideCompletion19383Variant {
  return ab.experimentVariant(
    Experiment.exp_guide_completion_19383,
  ) as GuideCompletion19383Variant
}

/**
 * Return whether the guide completion popup should be used.
 */
export function guideCompletion19383Treatment(): boolean {
  return guideCompletion19383Variant() === 'B'
}

/*
 * Start the exp_mini_player_19040 experiment.
 *
 * A - Control: the audio player only exists on the reading page.
 * B - Variant: show the Mini Player outside the reading pages.
 */
export async function miniPlayer19040Experiment(): Promise<MiniPlayer19040Variant> {
  let result = ab.experimentVariant(Experiment.exp_mini_player_19040)

  if (result === null) {
    result = await ab.start(Experiment.exp_mini_player_19040)
  }

  playerSession.value.canShowMiniPlayer = result === 'B'

  return result as MiniPlayer19040Variant
}

/**
 * Get the assigned variant for exp_mini_player_19040.
 */
export function miniPlayer19040Variant(): MiniPlayer19040Variant {
  return ab.experimentVariant(
    Experiment.exp_mini_player_19040,
  ) as MiniPlayer19040Variant
}

/**
 * Start the exp_save_for_later_1_library_20321 experiment.
 *
 * A - Control: current My Library page.
 * B - Variant: new unified page (no separate tabs for Books, Articles, etc).
 */
export async function saveForLater1Library20321Experiment(): Promise<SaveForLater1Library20321Variant> {
  let result = ab.experimentVariant(
    Experiment.exp_save_for_later_1_library_20321,
  )

  if (result === null) {
    result = await ab.start(Experiment.exp_save_for_later_1_library_20321)
  }

  return result as SaveForLater1Library20321Variant
}

/**
 * Get the assigned variant for exp_save_for_later_1_library_20321.
 */
export function saveForLater1Library20321Variant(): SaveForLater1Library20321Variant {
  return ab.experimentVariant(
    Experiment.exp_save_for_later_1_library_20321,
  ) as SaveForLater1Library20321Variant
}

/**
 * Start the exp_save_for_later_2_save_20322 experiment.
 *
 * A - Control: existing save and list controls.
 * B - Variant: new Save action and destination menu.
 */
export async function saveForLater2Save20322Experiment(): Promise<SaveForLater2Save20322Variant> {
  const result = ab.experimentVariant(
    Experiment.exp_save_for_later_2_save_20322,
  )

  // Uncomment and change `const` to `let` above when we start the experiment.
  // if (result === null) {
  //   result = await ab.start(Experiment.exp_save_for_later_2_save_20322)
  // }

  return result as SaveForLater2Save20322Variant
}

/**
 * Get the assigned variant for exp_save_for_later_2_save_20322.
 */
export function saveForLater2Save20322Variant(): SaveForLater2Save20322Variant {
  return ab.experimentVariant(
    Experiment.exp_save_for_later_2_save_20322,
  ) as SaveForLater2Save20322Variant
}

/**
 * Start the exp_onboarding_plan_cc_capture_20192 experiment.
 *
 * A - Control: existing flow (email capture -> onboarding -> billing page).
 * B - Variant: plan + card screen right after email capture; onboarding
 *     unchanged; context-aware landing after onboarding.
 *
 * Excluded: native app users, and users who actually entered onboarding
 * through the homepage hero goal selector (not just users bucketed into
 * exp_homepage_hero_goal_selector_19253 -- being assigned to that experiment
 * doesn't mean they saw or used the widget on this visit).
 */
export async function onboardingPlanCcCapture20192Experiment(): Promise<OnboardingPlanCcCapture20192Variant> {
  if (await platform.isNativeApp()) {
    return null
  }

  const enteredViaHeroGoalSelector = storage.getItem(
    StorageKeys.homepageHeroGoalSelectedValue19253,
  )
  if (enteredViaHeroGoalSelector) {
    return null
  }

  let result = ab.experimentVariant(
    Experiment.exp_onboarding_plan_cc_capture_20192,
  )

  if (result === null) {
    result = await ab.start(Experiment.exp_onboarding_plan_cc_capture_20192)
  }

  return result as OnboardingPlanCcCapture20192Variant
}

/**
 * Get the assigned variant for exp_onboarding_plan_cc_capture_20192.
 */
export function onboardingPlanCcCapture20192Variant(): OnboardingPlanCcCapture20192Variant {
  return ab.experimentVariant(
    Experiment.exp_onboarding_plan_cc_capture_20192,
  ) as OnboardingPlanCcCapture20192Variant
}

/**
 * Get the assigned variant for exp_hard_gate_billing_20516.
 */
export function hardGateBilling20516Variant(): HardGateBilling20516Variant {
  return ab.experimentVariant(
    Experiment.exp_hard_gate_billing_20516,
  ) as HardGateBilling20516Variant
}
